import { c as _c } from "react/compiler-runtime";
import { useChatStatus } from '@ai-sdk-tools/store';
import { ExternalLink } from 'lucide-react';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import { Streamdown, type Components } from 'streamdown';
import { preprocessMarkdownCitations } from '../../../lib/preprocess-citations';
import { remarkPlugins, shikiTheme, streamdownPlugins } from '../../../lib/streamdown-config';
import { InlineCitation, InlineCitationCard, InlineCitationCardBody, InlineCitationCardTrigger, InlineCitationCarousel, InlineCitationCarouselContent, InlineCitationCarouselHeader, InlineCitationCarouselIndex, InlineCitationCarouselItem, InlineCitationCarouselNext, InlineCitationCarouselPrev, InlineCitationSource, InlineCitationText } from '../../ai-elements';
import { type TableData } from '../../markdown-table/lib';
import { TableWrapper } from '../../markdown-table/table-wrapper';
import { getSourceMetadata, type SourcesMap } from '../lib/sources';
interface MarkdownWithCitationsProps {
  children: string;
  sourcesMap: SourcesMap;
  messageUuid?: string;
  className?: string;
  /** Callback when user clicks expand on a table (opens in spreadsheet view) */
  onExpandTable?: (tableData: TableData) => void;
  /** Prefix for analytics event names */
  prefix?: string;
}

/**
 * Citation component rendered from preprocessed <citation> HTML tags.
 * Receives sourceId from the AST node and looks up source data from the map.
 */
interface CitationComponentProps {
  sourceId: string;
  sourcesMap: SourcesMap;
  sourceIndexMap: Map<string, number>;
}
function CitationComponent(t0) {
  const $ = _c(70);
  const {
    sourceId,
    sourcesMap,
    sourceIndexMap
  } = t0;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let T6;
  let metadata;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== sourceId || $[1] !== sourceIndexMap || $[2] !== sourcesMap) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const source = sourcesMap.get(sourceId);
      if (!source) {
        let t9;
        if ($[19] !== sourceId) {
          t9 = <span>[SOURCE-{sourceId}]</span>;
          $[19] = sourceId;
          $[20] = t9;
        } else {
          t9 = $[20];
        }
        t8 = t9;
        break bb0;
      }
      metadata = getSourceMetadata(source);
      let t9;
      if ($[21] !== sourceId || $[22] !== sourceIndexMap) {
        t9 = sourceIndexMap.get(sourceId) ?? 0;
        $[21] = sourceId;
        $[22] = sourceIndexMap;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      const index = t9;
      T6 = InlineCitation;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <InlineCitationText />;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      T5 = InlineCitationCard;
      let t10;
      if ($[25] !== source) {
        t10 = [source.url];
        $[25] = source;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      if ($[27] !== index || $[28] !== t10) {
        t6 = <InlineCitationCardTrigger sources={t10}>{index}</InlineCitationCardTrigger>;
        $[27] = index;
        $[28] = t10;
        $[29] = t6;
      } else {
        t6 = $[29];
      }
      T4 = InlineCitationCardBody;
      T3 = InlineCitationCarousel;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <InlineCitationCarouselHeader><InlineCitationCarouselPrev /><InlineCitationCarouselIndex /><InlineCitationCarouselNext /></InlineCitationCarouselHeader>;
        $[30] = t5;
      } else {
        t5 = $[30];
      }
      T2 = InlineCitationCarouselContent;
      T1 = InlineCitationCarouselItem;
      T0 = InlineCitationSource;
      t1 = source.title;
      t2 = metadata?.textSnippet;
      let t11;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = <ExternalLink className="h-4 w-4" />;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      if ($[32] !== source) {
        t3 = <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary absolute top-0 right-0 transition-colors" onClick={_temp}>{t11}</a>;
        $[32] = source;
        $[33] = t3;
      } else {
        t3 = $[33];
      }
      t4 = metadata?.fileDate && <div className="text-muted-foreground text-xs">Date: {new Date(metadata.fileDate).toLocaleDateString()}</div>;
    }
    $[0] = sourceId;
    $[1] = sourceIndexMap;
    $[2] = sourcesMap;
    $[3] = T0;
    $[4] = T1;
    $[5] = T2;
    $[6] = T3;
    $[7] = T4;
    $[8] = T5;
    $[9] = T6;
    $[10] = metadata;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    T0 = $[3];
    T1 = $[4];
    T2 = $[5];
    T3 = $[6];
    T4 = $[7];
    T5 = $[8];
    T6 = $[9];
    metadata = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[34] !== metadata) {
    t9 = metadata?.emailFrom && <div className="text-muted-foreground text-xs">From: {metadata.emailFrom}</div>;
    $[34] = metadata;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== metadata) {
    t10 = metadata?.pageNumber && <div className="text-muted-foreground text-xs">Page: {String(metadata.pageNumber)}</div>;
    $[36] = metadata;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== metadata) {
    t11 = metadata?.sectionTitle && <div className="text-muted-foreground text-xs">Section: {String(metadata.sectionTitle)}</div>;
    $[38] = metadata;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== T0 || $[41] !== t1 || $[42] !== t10 || $[43] !== t11 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t9) {
    t12 = <T0 title={t1} description={t2}>{t3}{t4}{t9}{t10}{t11}</T0>;
    $[40] = T0;
    $[41] = t1;
    $[42] = t10;
    $[43] = t11;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t9;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== T1 || $[50] !== t12) {
    t13 = <T1>{t12}</T1>;
    $[49] = T1;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== T2 || $[53] !== t13) {
    t14 = <T2>{t13}</T2>;
    $[52] = T2;
    $[53] = t13;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== T3 || $[56] !== t14 || $[57] !== t5) {
    t15 = <T3>{t5}{t14}</T3>;
    $[55] = T3;
    $[56] = t14;
    $[57] = t5;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== T4 || $[60] !== t15) {
    t16 = <T4>{t15}</T4>;
    $[59] = T4;
    $[60] = t15;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== T5 || $[63] !== t16 || $[64] !== t6) {
    t17 = <T5>{t6}{t16}</T5>;
    $[62] = T5;
    $[63] = t16;
    $[64] = t6;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== T6 || $[67] !== t17 || $[68] !== t7) {
    t18 = <T6>{t7}{t17}</T6>;
    $[66] = T6;
    $[67] = t17;
    $[68] = t7;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  return t18;
}

/**
 * Create component overrides for markdown with citations.
 * Uses preprocessed <citation> tags (via preprocessMarkdownCitations) that are
 * handled at the AST level, so we only need minimal component overrides.
 */
function _temp(e) {
  return e.stopPropagation();
}
function createComponents(sourcesMap: SourcesMap, sourceIndexMap: Map<string, number>, messageUuid?: string, onExpandTable?: (tableData: TableData) => void, prefix?: string): Partial<Components> {
  return {
    // Citation elements created by preprocessMarkdownCitations
    // @ts-expect-error - 'citation' is a custom element not in standard Components
    citation: ({
      sourceid
    }: {
      sourceid: string;
    }) => <CitationComponent sourceId={sourceid} sourcesMap={sourcesMap} sourceIndexMap={sourceIndexMap} />,
    // Links use Next.js Link for client-side navigation
    a: ({
      children,
      href,
      ...props
    }) => <Link className="text-brand-400 hover:underline" target="_blank" rel="noreferrer" href={href ?? '#'} {...props}>
        {children}
      </Link>,
    // Table with analytics + expand callback
    table: ({
      children,
      ...props
    }) => <TableWrapper onExpandTable={onExpandTable} prefix={prefix} messageUuid={messageUuid} {...props}>
        {children}
      </TableWrapper>
  };
}

// Allow custom <citation> HTML tags through the sanitizer with their attributes
const allowedTags = {
  citation: ['sourceid']
};
function NonMemoizedMarkdownWithCitations({
  children,
  sourcesMap,
  messageUuid,
  className,
  onExpandTable,
  prefix
}: MarkdownWithCitationsProps) {
  const status = useChatStatus();
  const isStreaming = status === 'streaming';

  // Pre-process markdown to convert reference-style citation links
  const processedMarkdown = useMemo(() => preprocessMarkdownCitations(children), [children]);

  // Create sources array (preserves insertion order)
  const sourcesArray = useMemo(() => Array.from(sourcesMap.values()), [sourcesMap]);

  // Create sourceId -> 1-based index map for O(1) lookup
  const sourceIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sourcesArray.forEach((source, index) => {
      map.set(source.sourceId, index + 1);
    });
    return map;
  }, [sourcesArray]);

  // Memoize components to avoid recreation on every render
  const components = useMemo(() => createComponents(sourcesMap, sourceIndexMap, messageUuid, onExpandTable, prefix), [sourcesMap, sourceIndexMap, messageUuid, onExpandTable, prefix]);
  return <Streamdown plugins={streamdownPlugins} remarkPlugins={remarkPlugins} shikiTheme={shikiTheme} allowedTags={allowedTags} components={components} className={className} isAnimating={isStreaming} animated={isStreaming}>
      {processedMarkdown}
    </Streamdown>;
}
function areSourcesMapsEqual(prev: SourcesMap, next: SourcesMap): boolean {
  if (prev === next) return true;
  if (prev.size !== next.size) return false;
  return Array.from(prev.entries()).every(([sourceId, prevSource]) => {
    const nextSource = next.get(sourceId);
    if (!nextSource) return false;
    if (prevSource.url !== nextSource.url || prevSource.title !== nextSource.title) return false;
    const prevMetadata = getSourceMetadata(prevSource);
    const nextMetadata = getSourceMetadata(nextSource);
    return prevMetadata?.textSnippet === nextMetadata?.textSnippet && prevMetadata?.fileDate === nextMetadata?.fileDate && prevMetadata?.emailFrom === nextMetadata?.emailFrom;
  });
}
export const MarkdownWithCitations = memo(NonMemoizedMarkdownWithCitations, (prevProps, nextProps) => prevProps.children === nextProps.children && areSourcesMapsEqual(prevProps.sourcesMap, nextProps.sourcesMap) && prevProps.messageUuid === nextProps.messageUuid && prevProps.className === nextProps.className);